import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import Modal from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import CoverageFormFields from '@/features/Quote/components/settings/coverage/CoverageFormFields';
import { DEFAULT_COVERAGE_VALUES } from '@/features/Quote/components/settings/coverage/constants';
import { detectUnconventionalConfig } from '@/features/Quote/services/coverageRules';
import apiService from '@/services/apiService';
import type { CoverageValues } from '@/types/app';

interface SlstCoverageProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const SlstCoverage = ({ open, onOpenChange }: SlstCoverageProps) => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const { setAppData } = useAppContext();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [values, setValues] = useState<CoverageValues>({ ...DEFAULT_COVERAGE_VALUES });

	const unconventionalDetection = useMemo(() => detectUnconventionalConfig(values), [values]);

	const fieldErrors = useMemo(() => {
		const errors: Record<string, string> = {};
		if (!unconventionalDetection.hasIssues) return errors;
		for (const rule of unconventionalDetection.triggeredRules) {
			switch (rule.type) {
				case 'excessive_waiting_days':
					errors.waiting_period = t('quotes.coverageWarning.excessiveWaiting90');
					break;
				case 'unusual_waiting_period': {
					const fix = rule.suggestFix(values);
					errors.waiting_period = t('quotes.coverageWarning.unusualWaitingPeriod', {
						days: fix.waiting_period
					});
					break;
				}
				case 'reverse_coverage_progression':
					errors.coverage_all = t('quotes.coverageWarning.reverseCoverage');
					break;
				case 'low_coverage':
					errors.coverage_all = t('quotes.coverageWarning.lowCoverage');
					break;
				case 'high_or_decimal_employer_costs':
					errors.employer_costs = t('quotes.coverageWarning.highOrDecimalEmployerCosts');
					break;
			}
		}
		return errors;
	}, [unconventionalDetection, t, values]);

	const handleChange = (field: keyof CoverageValues, value: number) => {
		setValues((prev) => ({ ...prev, [field]: value }));
	};

	const handleSubmit = async () => {
		if (!id) return;
		setIsSubmitting(true);
		setError(null);

		try {
			const response = await apiService.slstCoverage.update(id, {
				sick_leave_short_term: {
					waiting_period: values.waiting_period,
					employer_costs: values.employer_costs,
					coverage_percentage_first_year_first_half:
						values.coverage_percentage_first_year_first_half,
					coverage_percentage_first_year_second_half:
						values.coverage_percentage_first_year_second_half,
					coverage_percentage_second_year_first_half:
						values.coverage_percentage_second_year_first_half,
					coverage_percentage_second_year_second_half:
						values.coverage_percentage_second_year_second_half
				}
			});
			if (response) setAppData(response);
			onOpenChange(false);
		} catch (err) {
			console.error('SLST coverage submit error:', err);
			setError(t('modals.slstCoverage.errorMessage'));
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			id="slst-coverage"
			open={open}
			onOpenChange={onOpenChange}
			backdrop="static"
			keyboard={false}
			closeButton={false}
			size="xl"
		>
			<div className="p-6">
				<h2 className="text-xl font-bold mb-1">{t('modals.slstCoverage.title')}</h2>
				<p className="text-muted-foreground mb-5">{t('modals.slstCoverage.subtitle')}</p>

				{error && (
					<div className="mb-4 p-3 bg-destructive/10 text-destructive text-sm rounded-md">
						{error}
					</div>
				)}

				<CoverageFormFields
					values={values}
					onChange={handleChange}
					fieldErrors={fieldErrors}
					testIdPrefix="slst-modal"
				/>

				<Button
					type="button"
					className="w-full mt-2"
					onClick={handleSubmit}
					disabled={isSubmitting}
					loading={isSubmitting}
					loadingText={t('modals.slstCoverage.submitting')}
					data-testid="slst-modal-submit"
				>
					{t('modals.slstCoverage.submitButton')}
				</Button>
			</div>
		</Modal>
	);
};

export default SlstCoverage;
